import { type FormEvent, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { MeResponse } from "../../shared/api-types";
import { loginSchema } from "../../shared/schemas";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useAuthOptions } from "../auth/useAuthOptions";
import { GOOGLE_MESSAGES, GoogleButton } from "../components/GoogleButton";
import { Alert, AuthLayout, focusFirstInvalid, TextField } from "../components/ui";
import { apiFailure, type FieldErrors, validate } from "./forms";

export function safeNext(next: string | null): string {
  // The app's own pages, or a prepared restaurant's claim link (owner 2026-11-04).
  return next?.startsWith("/app") || /^\/preluare\/[A-Za-z0-9_-]{43}$/.test(next ?? "")
    ? (next as string)
    : "/app";
}

export function LoginPage() {
  const { signedIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState("");
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);
  const [busy, setBusy] = useState(false);
  const options = useAuthOptions();
  const googleReason = params.get("google");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMessage("");
    setUnverified(false);
    const v = validate(loginSchema, { email, password });
    if (!v.ok) {
      setErrors(v.errors);
      focusFirstInvalid(formRef.current);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      signedIn(await api<MeResponse>("POST", "/auth/login", v.data));
      navigate(safeNext(params.get("next")), { replace: true });
    } catch (err) {
      const f = apiFailure(err);
      setErrors(f.fields);
      setMessage(f.message);
      setUnverified(f.code === "email_not_verified");
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    try {
      await api("POST", "/auth/resend-verification", { email: email.trim() });
      setResent(true);
    } catch (err) {
      setMessage(apiFailure(err).message);
    }
  }

  return (
    <AuthLayout>
      <div className="stack" style={{ gap: 8 }}>
        <h1 className="h1">Intră în cont</h1>
        <p className="muted" style={{ margin: 0 }}>
          Administrează meniul restaurantului tău.
        </p>
      </div>
      {params.get("reset") === "1" && (
        <Alert tone="success" title="Parola a fost schimbată." live>
          Intră cu parola nouă.
        </Alert>
      )}
      {googleReason && (
        <Alert tone="error" live>
          {GOOGLE_MESSAGES[googleReason] ?? GOOGLE_MESSAGES.token}
        </Alert>
      )}
      {options?.googleEnabled && <GoogleButton />}
      {message && (
        <Alert tone="error" live>
          {message}{" "}
          {unverified && !resent && (
            <button type="button" className="link-btn" onClick={resend}>
              Trimite din nou linkul
            </button>
          )}
        </Alert>
      )}
      {resent && (
        <Alert tone="info" live>
          Dacă adresa are un cont neconfirmat, ți-am trimis un link nou.
        </Alert>
      )}
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <TextField
          id="login-email"
          label="E-mail"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
        />
        <TextField
          id="login-password"
          label="Parolă"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
        />
        <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
          {busy ? "Se verifică…" : "Intră în cont"}
        </button>
      </form>
      {options && (
        <div className="auth-foot stack" style={{ gap: 8 }}>
          {options.emailEnabled ? (
            <Link to="/resetare-parola">Ai uitat parola?</Link>
          ) : (
            <span>Ai uitat parola? Cere echipei Meniova o invitație nouă.</span>
          )}
          {options.registrationOpen ? (
            <span>
              Nu ai cont? <Link to="/inregistrare">Creează un cont</Link>
            </span>
          ) : (
            <span>Conturile noi se creează momentan doar prin invitație.</span>
          )}
        </div>
      )}
    </AuthLayout>
  );
}
